import { Component, OnInit, inject, ChangeDetectorRef, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { AppPrimeTable, PrimeTableColumn } from '../../../../shared/components/prime-table/prime-table';
import { AppDropdown } from '../../../../shared/components/dropdown/dropdown';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LaboratoryService } from '../../../../core/services/laboratory.service';
import { Store } from '@ngrx/store';
import { selectBranchId } from '../../../../core/store/auth/auth.selectors';
import { LaboratoryModalComponent } from '../laboratory-modal/laboratory-modal';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';

@Component({
  selector: 'app-laboratory-list',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    AppPrimeTable, 
    AppDropdown,
    AppButton,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    TranslateModule,
    LaboratoryModalComponent,
    HasPermissionDirective
  ],
  templateUrl: './laboratory-list.html'
})
export class LaboratoryListComponent implements OnInit {
  private laboratoryService = inject(LaboratoryService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private perm = inject(PermissionService);

  @ViewChild('actionsTpl', { static: true }) actionsTpl!: TemplateRef<any>;

  laboratories: any[] = [];
  loading = true;
  searchControl = new FormControl('');
  branchId: number | null = null;
  
  // Pagination
  totalRecords = 0;
  rows = 10;
  first = 0;
  
  // Modal state
  showLaboratoryModal = false;
  selectedLaboratory: any = null;
  
  // Delete modal
  showDeleteModal = false;
  laboratoryToDelete: any = null;

  templates: { [key: string]: TemplateRef<any> } = {};

  columns: PrimeTableColumn[] = [
    { field: 'name', header: 'name', width: '30%' },
    { field: 'email', header: 'email', width: '25%' },
    { field: 'phone', header: 'phone', width: '20%' },
    { field: 'city', header: 'city', width: '15%' },
    { field: 'actions', header: 'actions', type: 'action', width: '10%' }
  ];

  ngOnInit() {
    this.templates = {
      'actions': this.actionsTpl
    };

    this.store.select(selectBranchId).subscribe(id => {
      this.branchId = id;
      if (id) {
        this.loadData();
      }
    });

    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(value => {
      this.first = 0;
      this.loadData(value || '');
    });
  }

  async loadData(search: string = '') {
    if (!this.branchId) return;
    this.loading = true;
    try {
      const result = await firstValueFrom(this.laboratoryService.getLaboratories(this.branchId, search, this.rows, this.first));
      this.laboratories = result.data;
      this.totalRecords = result.count || 0;
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData(this.searchControl.value || '');
  }

  onAdd() {
    if (!this.perm.canCreate(MODULE.LABORATORIOS)) return;
    this.selectedLaboratory = null;
    this.showLaboratoryModal = true;
  }

  onEdit(laboratory: any) {
    if (!this.perm.canEdit(MODULE.LABORATORIOS)) return;
    this.selectedLaboratory = laboratory;
    this.showLaboratoryModal = true;
  }

  openDeleteModal(laboratory: any) {
    if (!this.perm.canDelete(MODULE.LABORATORIOS)) return;
    this.laboratoryToDelete = laboratory;
    this.showDeleteModal = true;
  }

  async confirmDelete() {
    if (!this.laboratoryToDelete) return;

    try {
      await firstValueFrom(this.laboratoryService.deleteLaboratory(this.laboratoryToDelete.id));
      toast.success(this.translate.instant('laboratory_deleted'));
      this.loadData(this.searchControl.value || '');
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('laboratory_error_delete'));
    } finally {
      this.showDeleteModal = false;
      this.laboratoryToDelete = null;
    }
  }

  closeModal() {
    this.showLaboratoryModal = false;
    this.selectedLaboratory = null;
  }

  onView(laboratory: any) {
    this.router.navigate(['/laboratory', laboratory.id]);
  }

  onSaved() {
    this.loadData(this.searchControl.value || '');
    this.closeModal();
  }
}
